import type { PropsWithChildren } from "react";
import { motion, useReducedMotion, type HTMLMotionProps } from "framer-motion";
import { useMediaQuery } from "../hooks/useMediaQuery";

const motionElements = {
  article: motion.create("article"),
  div: motion.create("div"),
  h1: motion.create("h1"),
  h2: motion.create("h2"),
  h3: motion.create("h3"),
  li: motion.create("li"),
  nav: motion.create("nav"),
  p: motion.create("p"),
  section: motion.create("section"),
  ul: motion.create("ul"),
} as const;

type FadeInProps = PropsWithChildren<
  Omit<HTMLMotionProps<"div">, "initial" | "whileInView" | "viewport" | "transition"> & {
    as?: keyof typeof motionElements;
    delay?: number;
    duration?: number;
    x?: number;
    y?: number;
  }
>;

export function FadeIn({
  as = "div",
  children,
  delay = 0,
  duration = 0.7,
  x = 0,
  y = 30,
  ...props
}: FadeInProps) {
  const reduceMotion = useReducedMotion();
  const isPhone = useMediaQuery("(max-width: 767px)");
  const disableMotion = Boolean(reduceMotion) || isPhone;
  const MotionElement = motionElements[as] as typeof motion.div;

  return (
    <MotionElement
      initial={disableMotion ? false : { opacity: 0, x, y }}
      whileInView={disableMotion ? undefined : { opacity: 1, x: 0, y: 0 }}
      viewport={disableMotion ? undefined : { once: true, margin: "50px", amount: 0 }}
      transition={
        disableMotion ? undefined : { duration, delay, ease: [0.25, 0.1, 0.25, 1] }
      }
      {...props}
    >
      {children}
    </MotionElement>
  );
}
